import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { LifeBuoy } from 'lucide-react'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '#/components/ui/accordion'
import { Card, CardContent, CardHeader, CardTitle } from '#/components/ui/card'
import { PageHeader, SectionLabel } from '#/components/page-header'
import { PageContainer } from '#/components/page-container'

export const Route = createFileRoute('/_authed/accounts/$accountId/support')({
  component: SupportPage,
})

const STEP_KEYS = [
  'account',
  'workspace',
  'documents',
  'draft',
  'deliver',
] as const

const FAQ_KEYS = [
  'workspace',
  'documents',
  'drafting',
  'review',
  'knowledgeGraph',
  'access',
] as const

function SupportPage() {
  const { t } = useTranslation()
  const email = t('support.contact.email')

  return (
    <PageContainer width="reading">
      <PageHeader
        eyebrow={t('nav.support')}
        eyebrowIcon={LifeBuoy}
        title={t('support.title')}
      />

      <section className="flex flex-col gap-3">
        <SectionLabel>{t('support.gettingStartedLabel')}</SectionLabel>
        <p className="text-sm text-muted-foreground">
          {t('support.gettingStartedIntro')}
        </p>
        <ol className="flex flex-col gap-3">
          {STEP_KEYS.map((key, i) => (
            <li key={key} className="flex gap-3 text-sm">
              <span
                aria-hidden="true"
                className="w-5 shrink-0 pt-0.5 text-right font-serif text-heading-md font-normal text-title tabular-nums"
              >
                {i + 1}
              </span>
              <span className="pt-0.5">{t(`support.steps.${key}`)}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="flex flex-col gap-3">
        <SectionLabel>{t('support.faqsLabel')}</SectionLabel>
        <Accordion type="single" collapsible>
          {FAQ_KEYS.map((key) => (
            <AccordionItem key={key} value={key}>
              <AccordionTrigger>{t(`support.faqs.${key}.q`)}</AccordionTrigger>
              <AccordionContent>{t(`support.faqs.${key}.a`)}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <Card>
        <CardHeader>
          <CardTitle>{t('support.contact.heading')}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          {t('support.contact.body')}{' '}
          <a
            href={`mailto:${email}`}
            className="font-medium text-foreground underline underline-offset-4"
          >
            {email}
          </a>
        </CardContent>
      </Card>
    </PageContainer>
  )
}
